import { isValidAddressForNetwork } from '@chainflip/bitcoin';
import { isValidSolanaAddress } from '@chainflip/solana';
import { CHAINFLIP_SS58_PREFIX } from '@chainflip/utils/consts';
import * as ss58 from '@chainflip/utils/ss58';
import { isHex } from '@chainflip/utils/string';
import { isValidTronAddress } from '@chainflip/utils/tron';
import { isAddress } from 'viem';
import { type ConfigChainId, type SupportedChainId } from '@/shared/assets/chains/index';
import {
  ethereum,
  bitcoin,
  arbitrum,
  solana,
  assethub,
  tron,
  bsc,
} from '@/shared/assets/chains/mainnet';
import {
  sepolia,
  bitcoinTestnet,
  arbitrumSepolia,
  backspinEthereum,
  backspinArbitrum,
  solanaDevnet,
  backspinSolana,
  hubTestnet,
  backspinTron,
  tronTestnet,
  bscTestnet,
  backspinBsc,
} from '@/shared/assets/chains/testnet';
import { getChainflipNetwork } from '@/shared/utils/env';

export type AddressValidator = (address: string) => boolean;

export const validatePolkadotAddress: AddressValidator = (address) => {
  try {
    if (isHex(address)) {
      ss58.encode({ data: address, ss58Format: CHAINFLIP_SS58_PREFIX });
    } else {
      ss58.decode(address);
    }
    return true;
  } catch {
    return false;
  }
};

export const validateEvmAddress: AddressValidator = isAddress;

type BitcoinNetwork = 'mainnet' | 'testnet' | 'regtest';

const validateBitcoinAddress = (address: string, network: BitcoinNetwork) =>
  isValidAddressForNetwork(address, network);

export const validateBitcoinMainnetAddress: AddressValidator = (address: string) =>
  validateBitcoinAddress(address, 'mainnet');

export const validateBitcoinTestnetAddress: AddressValidator = (address: string) =>
  validateBitcoinAddress(address, 'testnet');

export const validateBitcoinRegtestAddress: AddressValidator = (address: string) =>
  validateBitcoinAddress(address, 'regtest');

export const validateChainAddress = (address: string): Record<SupportedChainId, boolean> => {
  const isValidEvmAddress = validateEvmAddress(address);
  const isValidPolkadotAddress = validatePolkadotAddress(address);
  const chainflipNetwork = getChainflipNetwork();

  return {
    [ethereum.id]: isValidEvmAddress,
    [bitcoin.id]: validateBitcoinMainnetAddress(address),
    [solana.id]: isValidSolanaAddress(address),
    [assethub.id]: isValidPolkadotAddress,
    [arbitrum.id]: isValidEvmAddress,
    [tron.id]: isValidTronAddress(address),
    [bsc.id]: isValidEvmAddress,
    // // testnet
    [sepolia.id]: isValidEvmAddress,
    [backspinEthereum.id]: isValidEvmAddress,
    [backspinArbitrum.id]: isValidEvmAddress,
    [bitcoinTestnet.id]:
      chainflipNetwork === 'backspin'
        ? validateBitcoinRegtestAddress(address)
        : validateBitcoinTestnetAddress(address),
    [solanaDevnet.id]: isValidSolanaAddress(address),
    [backspinSolana.id]: isValidSolanaAddress(address),
    [hubTestnet.id]: isValidPolkadotAddress,
    [arbitrumSepolia.id]: isValidEvmAddress,
    [tronTestnet.id]: isValidTronAddress(address),
    [backspinTron.id]: isValidTronAddress(address),
    [bscTestnet.id]: isValidEvmAddress,
    [backspinBsc.id]: isValidEvmAddress,
  } satisfies Record<ConfigChainId, boolean>;
};
